/**
 * Keyboard-activation helpers for app-owned composite targets (HS2-GH2HK4,
 * HS2-YBZTVW): a focusable container (a document tile, a Markdown preview) that
 * reacts to Enter/Space must leave those keys to any interactive control nested
 * inside it — a button, a link in rendered Markdown, a form field — so the
 * control keeps its native activation.
 */

/** Interactive descendants that own their own Enter/Space activation. */
export const NESTED_CONTROL_SELECTOR = [
  'button',
  'a',
  'input',
  'select',
  'textarea',
  '[contenteditable="true"]',
  '[role="button"]',
  '[role="link"]',
  '[data-action]',
  'kui-select',
  'wa-switch',
  '.kui-popup-menu',
].join(', ');

/** The minimal DOM surface the helpers need (keeps them unit-testable without a DOM). */
interface ClosestTarget {
  closest: (selector: string) => unknown;
}
interface Container {
  contains: (other: never) => boolean;
}

function hasClosest(target: unknown): target is ClosestTarget {
  return (
    typeof target === 'object' && target !== null && typeof (target as { closest?: unknown }).closest === 'function'
  );
}

/**
 * True when `target` is (inside) an interactive control nested within
 * `container` — other than the container itself. A keydown with such a target
 * belongs to that control, not to the container's Enter/Space handling.
 */
export function isNestedControl(target: EventTarget | null, container: Element): boolean {
  if (!hasClosest(target)) return false;
  const control = target.closest(NESTED_CONTROL_SELECTOR);
  if (control === null || control === undefined || control === container) return false;
  return (container as unknown as Container).contains(control as never);
}

/** Which activation key a keydown is: `'enter'`, `'space'`, or `null` for any other key. */
export function activationKey(event: Pick<KeyboardEvent, 'key'>): 'enter' | 'space' | null {
  if (event.key === 'Enter') return 'enter';
  if (event.key === ' ' || event.key === 'Spacebar') return 'space';
  return null;
}
